import { OFFER_STATUSES, SERVICE_MODES } from '../enums';
import type { ReportSpecificFilter, ReportTableColumn } from '../report-runtime';
import { REPORT_PAYMENT_STATES, type ServiceJobDetailRow } from '../reports';

export type MissedServicesDateBasis = 'TERMINAL_FAILURE_EVENT' | 'DISPATCH_FAILED_AT' | 'UNAVAILABLE';
export type MissedServicesEvidenceState =
  | 'LINKED_SEARCH'
  | 'TEMPORAL_ASSOCIATION'
  | 'LATEST_AVAILABLE'
  | 'EXPIRED'
  | 'UNAVAILABLE';
export interface MissedServicesFailureFacts {
  failureEventId: string | null;
  failedAt: string | null;
  dateBasis: MissedServicesDateBasis;
  dispatchFailedAt: string | null;
  failureDateMismatch: boolean;
  failureActorType: string | null;
  failureActorId: string | null;
  failureActorName: string | null;
  failureActorPictureUrl: string | null;
  failureReasonCode: string | null;
  failureReasonId: string | null;
  failureNote: string | null;
  staffingDeadlineAt: string | null;
  latestSearchId: string | null;
  latestSearchState: string | null;
  latestSearchMode: string | null;
  latestSearchStrategy: string | null;
  latestAttemptId: string | null;
  latestAttemptRevision: number | null;
  latestOfferId: string | null;
  latestOfferKind: string | null;
  latestOfferStatus: string | null;
  latestOfferOfferedAt: string | null;
  latestOfferRespondedAt: string | null;
  dispatchEvidenceState: MissedServicesEvidenceState;
  searchAssociation: 'LINKED_SEARCH' | 'TEMPORAL_ASSOCIATION' | 'LATEST_AVAILABLE' | 'UNAVAILABLE';
}
export interface MissedServicesRow {
  trip: ServiceJobDetailRow;
  bookingId: string;
  pickupApptAt: string | null;
  arriveByAt: string | null;
  failure: MissedServicesFailureFacts;
}
export interface MissedServicesFilters {
  failureReason?: string;
  latestSearchMode?: string[];
  latestSearchStrategy?: string[];
  latestSearchState?: string[];
  latestOfferState?: string[];
  evidenceState?: MissedServicesEvidenceState[];
  hasStaffingDeadline?: boolean;
  missingEvent?: boolean;
  serviceMode?: string[];
  passengerId?: string;
  agentId?: string;
  paymentState?: string[];
  requiresFinanceReview?: boolean;
}
export const missedServicesFilters: readonly ReportSpecificFilter[] = [
  { id: 'failureReason', type: 'text' },
  { id: 'latestSearchMode', type: 'multi', values: ['AUTO', 'MANUAL'] },
  { id: 'latestSearchStrategy', type: 'multi', values: ['SEQUENTIAL', 'BROADCAST'] },
  {
    id: 'latestSearchState',
    type: 'multi',
    values: ['ACTIVE', 'ASSIGNED', 'EXHAUSTED', 'CANCELLED', 'SUPERSEDED', 'LEGACY_HISTORY'],
  },
  { id: 'latestOfferState', type: 'multi', values: OFFER_STATUSES },
  {
    id: 'evidenceState',
    type: 'multi',
    values: ['LINKED_SEARCH', 'TEMPORAL_ASSOCIATION', 'LATEST_AVAILABLE', 'EXPIRED', 'UNAVAILABLE'],
  },
  { id: 'hasStaffingDeadline', type: 'boolean' },
  { id: 'missingEvent', type: 'boolean' },
  { id: 'serviceMode', type: 'multi', values: SERVICE_MODES },
  { id: 'passengerId', type: 'uuid' },
  { id: 'agentId', type: 'uuid' },
  { id: 'paymentState', type: 'multi', values: REPORT_PAYMENT_STATES },
  { id: 'requiresFinanceReview', type: 'boolean' },
];
export const missedServicesSorts = ['failedAt', 'tripRef', 'serviceDate'] as const;
export const missedServicesDefaultColumns = [
  'provider_name',
  'trip_ref',
  'failed_at',
  'date_basis',
  'failure_reason_code',
  'passenger_name',
  'agent_name',
  'latest_search_state',
  'dispatch_evidence_state',
  'current_total',
  'received_total',
  'payment_state',
] as const;
const identity = [
  'provider_id',
  'provider_name',
  'trip_id',
  'trip_ref',
  'booking_id',
  'trip_status',
  'trip_leg',
  'service_mode',
  'dispatch_mode',
  'created_at',
  'service_date',
  'service_date_basis',
  'scheduled_start',
  'scheduled_end',
  'pickup_appt_at',
  'arrive_by_at',
] as const;
const failure = [
  'failure_event_id',
  'failed_at',
  'date_basis',
  'dispatch_failed_at',
  'failure_actor_type',
  'failure_actor_id',
  'failure_actor_name',
  'failure_reason_code',
  'failure_reason_id',
  'failure_note',
  'staffing_deadline_at',
  'latest_search_id',
  'latest_search_state',
  'latest_search_mode',
  'latest_search_strategy',
  'latest_attempt_id',
  'latest_attempt_revision',
  'latest_offer_id',
  'latest_offer_kind',
  'latest_offer_status',
  'latest_offer_offered_at',
  'latest_offer_responded_at',
  'dispatch_evidence_state',
  'search_association',
  'failure_date_mismatch',
] as const;
const people = [
  'passenger_id',
  'passenger_code',
  'passenger_name',
  'passenger_phone',
  'agent_id',
  'agent_code',
  'agent_name',
  'agent_phone',
  'vehicle_id',
  'vehicle_plate',
  'service_area_ids',
  'service_area_names',
  'service_group_id',
  'service_group_name',
  'scope_provenance',
  'origin_label',
  'destination_label',
] as const;
const finance = [
  'currency',
  'fare_state',
  'quote_provenance',
  'quoted_total',
  'current_total',
  'final_total',
  'fare_adjustment_delta',
  'fare_base',
  'fare_overage_distance',
  'fare_overage_time',
  'fare_surcharge_total',
  'fare_computed_at',
  'billing_distance_source',
  'billing_duration_source',
  'billable_distance_km',
  'billable_duration_min',
  'fare_is_free',
  'base_payment_status',
  'base_amount_due',
  'base_amount_received',
  'base_outstanding',
  'base_due_by',
  'base_verified_at',
  'overage_payment_status',
  'overage_amount_due',
  'overage_amount_received',
  'overage_outstanding',
  'overage_due_by',
  'overage_verified_at',
  'billed_total',
  'received_total',
  'outstanding_total',
  'payment_state',
  'requires_finance_review',
] as const;
export const missedServicesColumnIds = [...identity, ...failure, ...people, ...finance] as const;
export type MissedServicesColumnId = (typeof missedServicesColumnIds)[number];
const timestamps: readonly string[] = [
  'created_at',
  'service_date',
  'scheduled_start',
  'scheduled_end',
  'pickup_appt_at',
  'arrive_by_at',
  'failed_at',
  'dispatch_failed_at',
  'staffing_deadline_at',
  'latest_offer_offered_at',
  'latest_offer_responded_at',
  'fare_computed_at',
  'base_due_by',
  'base_verified_at',
  'overage_due_by',
  'overage_verified_at',
];
const money: readonly string[] = finance.filter(
  (id) =>
    id.includes('total') ||
    id.includes('amount') ||
    id.includes('outstanding') ||
    ['fare_base', 'fare_overage_distance', 'fare_overage_time', 'fare_adjustment_delta'].includes(id),
);
const pii: readonly string[] = [
  'passenger_id',
  'passenger_code',
  'passenger_name',
  'passenger_phone',
  'agent_id',
  'agent_code',
  'agent_name',
  'agent_phone',
  'vehicle_plate',
  'failure_actor_id',
  'failure_actor_name',
  'failure_note',
  'origin_label',
  'destination_label',
];
const meanings: Partial<Record<MissedServicesColumnId, string>> = {
  failed_at: 'missedServices_meaning_failedAt',
  date_basis: 'missedServices_meaning_failedAt',
  dispatch_failed_at: 'missedServices_meaning_failedAt',
  failure_date_mismatch: 'missedServices_meaning_mismatch',
  service_date: 'missedServices_meaning_serviceDate',
  service_date_basis: 'missedServices_meaning_serviceDate',
  dispatch_evidence_state: 'missedServices_meaning_evidence',
  search_association: 'missedServices_meaning_evidence',
  latest_attempt_id: 'missedServices_meaning_history',
  latest_attempt_revision: 'missedServices_meaning_history',
  latest_offer_id: 'missedServices_meaning_history',
  latest_search_id: 'missedServices_meaning_history',
  latest_offer_status: 'missedServices_meaning_history',
  latest_search_state: 'missedServices_meaning_history',
  scope_provenance: 'missedServices_meaning_scope',
  service_area_ids: 'missedServices_meaning_scope',
  service_area_names: 'missedServices_meaning_scope',
  final_total: 'missedServices_meaning_finance',
  payment_state: 'missedServices_meaning_finance',
  requires_finance_review: 'missedServices_meaning_finance',
  billed_total: 'missedServices_meaning_finance',
  outstanding_total: 'missedServices_meaning_finance',
  received_total: 'missedServices_meaning_finance',
  fare_adjustment_delta: 'missedServices_meaning_adjustment',
  quoted_total: 'missedServices_meaning_adjustment',
  quote_provenance: 'missedServices_meaning_adjustment',
};
const sortKeys: Partial<Record<MissedServicesColumnId, string>> = {
  failed_at: 'failedAt',
  trip_ref: 'tripRef',
  service_date: 'serviceDate',
};
export const missedServicesColumns: readonly ReportTableColumn[] = missedServicesColumnIds.map((id) => ({
  id,
  labelKey: `missedServices_column_${id}`,
  type: timestamps.includes(id)
    ? 'timestamp'
    : money.includes(id)
      ? 'money'
      : ['fare_is_free', 'requires_finance_review', 'failure_date_mismatch'].includes(id)
        ? 'boolean'
        : id === 'latest_attempt_revision' || id === 'billable_duration_min'
          ? 'number'
          : id === 'billable_distance_km'
            ? 'distance'
            : id === 'service_area_ids' || id === 'service_area_names'
              ? 'list'
              : 'text',
  pii: pii.includes(id),
  defaultVisible: (missedServicesDefaultColumns as readonly string[]).includes(id),
  source: finance.includes(id as (typeof finance)[number])
    ? 'missedServices_source_finance'
    : failure.includes(id as (typeof failure)[number])
      ? id.startsWith('failure_actor')
        ? 'missedServices_source_actor'
        : 'missedServices_source_failure'
      : people.includes(id as (typeof people)[number])
        ? 'missedServices_source_current'
        : 'missedServices_source_trip',
  emptyMeaning: id === 'agent_code' ? 'missedServices_empty_agentCode' : 'missedServices_empty_unavailable',
  meaningKey: meanings[id],
  sortable: sortKeys[id] !== undefined,
  sortKey: sortKeys[id],
  unitKey: id === 'billable_duration_min' ? 'unit_minutes' : undefined,
}));
export const missedServicesSummaryFields = [
  'missedTripCount',
  'fareTotalSum',
  'billedTotalSum',
  'receivedTotalSum',
  'outstandingTotalSum',
  'freeTripCount',
  'financeReviewCount',
  'missingFareCount',
  'missingFailureDateCount',
  'missingEventCount',
  'missingLinkedSearchCount',
  'expiredEvidenceCount',
  'failureDateMismatchCount',
  'unknownReasonCount',
] as const;
